/**
 * useBlockSelection — Manages block selection state from iframe PostMessages.
 *
 * Listens for BLOCK_SELECTED / BLOCK_DESELECTED / BLOCK_REORDER from the
 * storefront preview iframe. Exposes selected block info and a deselectBlock action.
 *
 * Also handles:
 * - Block content updates (cosmetic three-step sequence)
 * - Block reorder (from iframe DnD → local state + cosmetic skip)
 *
 * Part of Plan 145 — Block Editor Customizer, Epic 01 + Epic 02 + Epic 04.
 */

import { useState, useEffect, useCallback, useRef, type MutableRefObject } from 'react'
import { isAllowedStorefrontOrigin } from '../mobile-design/preview-protocol'
import {
  MSG_BLOCK_SELECTED,
  MSG_BLOCK_DESELECTED,
  MSG_BLOCK_REORDER,
  MSG_BLOCK_RESIZE,
  MSG_INSERT_BLOCK,
  MSG_REMOVE_BLOCK,
  MSG_BLOCK_CROSS_SECTION_MOVE,
} from './page-editor-protocol'
import { BLOCK_TYPE_WIDTH_DEFAULTS } from '@zaviago/storefront-config'
import type { BlockContent, BlockType, BlockNode, SectionEnvelope } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'

export interface SelectedBlockInfo {
  sectionPath: string
  blockId: string
  blockType: string
  content: BlockContent
  /** Current width percentage of the block (from layout.widthPct) */
  widthPct: number
}

interface UseBlockSelectionOpts {
  storefrontUrl: string | null
  findByEditorId: (id: string) => { section: SectionEnvelope } | null
  updateSection: (editorId: string, patch: Partial<SectionEnvelope>) => void
  sendBlockUpdate: (sectionPath: string, blockId: string, content: BlockContent) => void
  cosmeticSkipCountRef: MutableRefObject<number>
  selectedSectionId: string | null
  /** Ref to the current sections array — needed to map sectionPath to editorId */
  sectionsRef: MutableRefObject<EditorSection[]>
  /** Block insert handler from useBlockOperations */
  onInsertBlock?: (sectionPath: string, blockType: BlockType, insertAtIndex: number, blockIdPrefix?: string) => void
  /** Block remove handler from useBlockOperations */
  onRemoveBlock?: (sectionPath: string, blockId: string) => void
  /** Push undo entry from useBlockOperations (for reorder undo support) */
  onPushUndo?: (sectionEditorId: string, previousBlocks: BlockNode[]) => void
  /** Add a new section with initial blocks at a specific index (Plan 149 — gap drop) */
  addSectionWithBlocks?: (type: string, insertIndex: number, initialBlocks: BlockNode[]) => string | null
  /** Push compound undo entry for cross-section moves (Plan 149 — atomic multi-section undo) */
  onPushCompoundUndo?: (entries: Array<{ sectionEditorId: string; previousBlocks: BlockNode[] }>) => void
  /** Push gap-drop undo entry: restores source blocks + removes auto-created section (Plan 150 Epic 04) */
  onPushGapDropUndo?: (sourceEditorId: string, previousBlocks: BlockNode[], createdSectionEditorId: string) => void
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Grouped widget types and their default groupId (Plan 149 Epic 02 — groupId transitions) */
const GROUPED_WIDGET_DEFAULT_GROUP: Record<string, string> = {
  'code:exogro-header': 'navbar',
  'code:exogro-footer': 'footer',
}

/** Get the default groupId for a section type (undefined = non-grouped) */
function getDefaultGroupId(sectionType: string): string | undefined {
  return GROUPED_WIDGET_DEFAULT_GROUP[sectionType]
}

/** Whether a section type uses naturalSizing (grouped widgets like header/footer) */
function isNaturalSizingSection(sectionType: string): boolean {
  return sectionType in GROUPED_WIDGET_DEFAULT_GROUP
}

/**
 * Width adaptation for blocks entering standard (percentage-width) sections.
 * Uses block-type-aware defaults from BLOCK_TYPE_WIDTH_DEFAULTS (user story Decision 3).
 * If the block already has a reasonable width (≥ 10%), it is preserved.
 */
function clampWidthPctForStandardSection(block: { type: string; layout: { widthPct: number; heightPx?: number } }): number {
  const typeDefault = BLOCK_TYPE_WIDTH_DEFAULTS[block.type as BlockType] ?? 100
  // Small-height blocks (e.g. 40px navbar logos) were designed for compact nav layouts.
  // In a standard section, their original widthPct (e.g. 25% = 350px) is way too large.
  // Use the type default which is designed for standard sections.
  if (block.layout.heightPx && block.layout.heightPx <= 60) return typeDefault
  // Keep the block's original widthPct if it's reasonable (≥ 10%)
  if (block.layout.widthPct >= 10) return block.layout.widthPct
  // Otherwise use the type-aware default
  return typeDefault
}

// Map sectionPath ("0", "2.1") to editorId

function sectionPathToEditorId(
  sections: EditorSection[],
  sectionPath: string,
): string | null {
  const parts = sectionPath.split('.').map(Number)
  let current: EditorSection | undefined = sections[parts[0]]
  for (let i = 1; i < parts.length && current?.children; i++) {
    current = current.children[parts[i]]
  }
  return current?._editorId ?? null
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useBlockSelection(opts: UseBlockSelectionOpts) {
  const [selectedBlock, setSelectedBlock] = useState<SelectedBlockInfo | null>(null)

  // Store opts in a ref so the effect handler always reads fresh values
  const optsRef = useRef(opts)
  optsRef.current = opts

  // Derive storefront origin for validation
  let targetOrigin = ''
  try {
    if (opts.storefrontUrl) targetOrigin = new URL(opts.storefrontUrl).origin
  } catch { /* ignore */ }

  useEffect(() => {
    function handler(event: MessageEvent) {
      if (!event.data?.type) return
      if (targetOrigin && !isAllowedStorefrontOrigin(event.origin, targetOrigin)) return

      const o = optsRef.current

      if (event.data.type === MSG_BLOCK_SELECTED) {
        // Look up widthPct from parent state (not sent in PostMessage)
        const edId = sectionPathToEditorId(o.sectionsRef.current, event.data.sectionPath)
        const lk = edId ? o.findByEditorId(edId) : null
        const bn = lk?.section?.blocks?.find((b: BlockNode) => b.id === event.data.blockId)
        setSelectedBlock({
          sectionPath: event.data.sectionPath,
          blockId: event.data.blockId,
          blockType: event.data.blockType,
          content: event.data.content,
          widthPct: bn?.layout?.widthPct ?? 100,
        })
      }
      if (event.data.type === MSG_BLOCK_DESELECTED) {
        setSelectedBlock(null)
      }

      // Block reorder from iframe DnD (same-group or cross-group)
      if (event.data.type === MSG_BLOCK_REORDER) {
        const { sectionPath, blockIds, groupChanges } = event.data as {
          sectionPath: string
          blockIds: string[]
          groupChanges?: Record<string, string>
        }
        handleBlockReorder(sectionPath, blockIds, groupChanges)
      }

      // Block resize from iframe drag handle
      if (event.data.type === MSG_BLOCK_RESIZE) {
        const { sectionPath, blockId, widthPct } = event.data as {
          sectionPath: string
          blockId: string
          widthPct: number
        }
        handleBlockResize(sectionPath, blockId, widthPct)
      }

      // Block insert from iframe insertion point
      if (event.data.type === MSG_INSERT_BLOCK) {
        const { sectionPath, blockType, insertAtIndex, blockIdPrefix } = event.data as {
          sectionPath: string
          blockType: BlockType
          insertAtIndex: number
          blockIdPrefix?: string
        }
        o.onInsertBlock?.(sectionPath, blockType, insertAtIndex, blockIdPrefix)
      }

      // Block remove from iframe (Delete key or trash icon)
      if (event.data.type === MSG_REMOVE_BLOCK) {
        const { sectionPath, blockId } = event.data as {
          sectionPath: string
          blockId: string
        }
        o.onRemoveBlock?.(sectionPath, blockId)
      }

      // Cross-section block move (Plan 149 — gap drop or section-to-section)
      if (event.data.type === MSG_BLOCK_CROSS_SECTION_MOVE) {
        const { sourceSectionPath, targetSectionPath, blockId, insertAtIndex, targetGroupId } = event.data as {
          sourceSectionPath: string
          targetSectionPath: string
          blockId: string
          insertAtIndex: number
          targetGroupId?: string // Plan 153 Epic 03: explicit group targeting
        }
        handleCrossSectionMove(sourceSectionPath, targetSectionPath, blockId, insertAtIndex, targetGroupId)
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetOrigin])

  const deselectBlock = useCallback(() => setSelectedBlock(null), [])

  /** Programmatically select a block (e.g. from sidebar block list row click). */
  const selectBlock = useCallback((info: SelectedBlockInfo) => setSelectedBlock(info), [])

  /** Three-step cosmetic update for block content changes. */
  const handleBlockUpdate = useCallback(
    (blockId: string, content: BlockContent) => {
      const { selectedSectionId, findByEditorId, updateSection, cosmeticSkipCountRef, sendBlockUpdate } = opts
      if (!selectedSectionId || !selectedBlock) return
      const lookup = findByEditorId(selectedSectionId)
      const section = lookup?.section
      if (!section?.blocks) return

      const updated = section.blocks.map((b: BlockNode) =>
        b.id === blockId ? { ...b, content } : b,
      )
      // Step 1: persist in local state
      updateSection(selectedSectionId, { blocks: updated })
      // Step 2: suppress structural CONFIG_UPDATE
      cosmeticSkipCountRef.current = 1
      // Step 3: send instant preview update
      sendBlockUpdate(selectedBlock.sectionPath, blockId, content)
      // Update local selection content
      setSelectedBlock((prev) => (prev ? { ...prev, content } : null))
    },
    [opts, selectedBlock],
  )

  /**
   * Handle block reorder from iframe DnD (same-group or cross-group).
   * Uses cosmetic skip — iframe already shows the new order,
   * so we only need to persist locally (no PostMessage back).
   *
   * When groupChanges is present (cross-group move), updates layout.groupId
   * on affected blocks and re-normalizes order per group.
   */
  const handleBlockReorder = useCallback(
    (sectionPath: string, blockIds: string[], groupChanges?: Record<string, string>) => {
      const o = optsRef.current

      // Map sectionPath to editorId
      const editorId = sectionPathToEditorId(o.sectionsRef.current, sectionPath)
      if (!editorId) return

      const lookup = o.findByEditorId(editorId)
      const section = lookup?.section
      if (!section?.blocks) return

      // Push undo BEFORE mutation (enables Cmd+Z for both same-group and cross-group)
      o.onPushUndo?.(editorId, section.blocks)

      // Reorder blocks to match the new blockIds order, applying groupChanges
      const blockMap = new Map(section.blocks.map(b => [b.id, b]))
      const reordered: BlockNode[] = []
      for (const id of blockIds) {
        const block = blockMap.get(id)
        if (!block) continue
        const newGroupId = groupChanges?.[block.id]
        reordered.push({
          ...block,
          layout: {
            ...block.layout,
            ...(newGroupId ? { groupId: newGroupId } : {}),
          },
        })
      }

      // Re-normalize order per group (0-based sequential within each group).
      // For same-group reorders this produces identical results to global numbering.
      const groups = new Map<string, BlockNode[]>()
      for (const block of reordered) {
        const gid = block.layout.groupId ?? block.id.split('-')[0]
        if (!groups.has(gid)) groups.set(gid, [])
        groups.get(gid)!.push(block)
      }
      const final: BlockNode[] = []
      for (const [, groupBlocks] of groups) {
        for (let i = 0; i < groupBlocks.length; i++) {
          final.push({
            ...groupBlocks[i],
            layout: { ...groupBlocks[i].layout, order: i },
          })
        }
      }

      // Persist in local state + suppress CONFIG_UPDATE bounce-back
      o.updateSection(editorId, { blocks: final })
      o.cosmeticSkipCountRef.current = 1
    },
    [],
  )

  /**
   * Handle block resize from iframe drag handle.
   * Uses cosmetic skip — iframe already shows the new width visually.
   */
  const handleBlockResize = useCallback(
    (sectionPath: string, blockId: string, widthPct: number) => {
      const o = optsRef.current

      const editorId = sectionPathToEditorId(o.sectionsRef.current, sectionPath)
      if (!editorId) return

      const lookup = o.findByEditorId(editorId)
      const section = lookup?.section
      if (!section?.blocks) return

      const updated = section.blocks.map((b: BlockNode) =>
        b.id === blockId ? { ...b, layout: { ...b.layout, widthPct } } : b,
      )

      // Step 1: persist in local state
      o.updateSection(editorId, { blocks: updated })
      // Step 2: suppress CONFIG_UPDATE bounce-back (iframe already shows correct width)
      o.cosmeticSkipCountRef.current = 1
      // Update local selection if this block is selected
      setSelectedBlock((prev) => (prev && prev.blockId === blockId ? { ...prev, widthPct } : prev))
    },
    [],
  )

  /**
   * Handle cross-section block move (Plan 149 — Universal Block Drag).
   * Removes block from source section, inserts into target section or creates
   * a new text-block section at a gap index.
   */
  const handleCrossSectionMove = useCallback(
    (sourceSectionPath: string, targetSectionPath: string, blockId: string, insertAtIndex: number, targetGroupId?: string) => {
      const o = optsRef.current

      // 1. Find source section and the block to move
      const sourceEditorId = sectionPathToEditorId(o.sectionsRef.current, sourceSectionPath)

      if (!sourceEditorId) return
      const sourceLookup = o.findByEditorId(sourceEditorId)
      const sourceSection = sourceLookup?.section
      if (!sourceSection?.blocks) return

      const block = sourceSection.blocks.find((b: BlockNode) => b.id === blockId)
      if (!block) return

      // 2. Remove block from source section
      const sourceBlocksAfter = sourceSection.blocks.filter((b: BlockNode) => b.id !== blockId)

      // 3. Handle gap drop (create new text-block section) vs section-to-section
      if (targetSectionPath.startsWith('gap:')) {
        const gapIndex = parseInt(targetSectionPath.slice(4), 10)
        if (isNaN(gapIndex)) return

        // Gap drops always create standard text-block sections — no enteringNatural case needed.
        // Clear groupId when moving out of a grouped widget.
        // Adjust widthPct when leaving a naturalSizing section (header/footer → standard).
        const sourceType = sourceSection.type ?? ''
        const needsWidthAdjust = isNaturalSizingSection(sourceType)
        const movedBlock: BlockNode = {
          ...block,
          layout: {
            ...block.layout,
            groupId: undefined,
            order: 0,
            ...(needsWidthAdjust ? { widthPct: clampWidthPctForStandardSection(block), heightPx: undefined } : {}),
          },
        }

        // Create new section first — need editorId for gap-drop undo (Plan 150 Epic 04)
        const createdEditorId = o.addSectionWithBlocks?.('text-block', gapIndex, [movedBlock])

        // If section creation failed, abort — don't remove block from source
        if (!createdEditorId) return

        // Push gap-drop undo entry: stores ORIGINAL source blocks (with moved block still present)
        // On Cmd+Z undo: source is restored to original (block returns) + created section is removed
        o.onPushGapDropUndo?.(sourceEditorId, sourceSection.blocks, createdEditorId)

        // Remove block from source
        o.updateSection(sourceEditorId, { blocks: sourceBlocksAfter })

        // Cross-section move: do NOT skip CONFIG_UPDATE — iframe needs to re-render with new block layout
        o.cosmeticSkipCountRef.current = 0
      } else {
        // Section-to-section move
        const targetEditorId = sectionPathToEditorId(o.sectionsRef.current, targetSectionPath)
        if (!targetEditorId || targetEditorId === sourceEditorId) return

        const targetLookup = o.findByEditorId(targetEditorId)
        const targetSection = targetLookup?.section
        if (!targetSection) return

        // groupId transition (Plan 149 Epic 02, extended Plan 150 Epic 02):
        // - Entering grouped widget (header/footer) → set explicit group or default
        // - Entering non-grouped section → clear groupId
        const sourceType = sourceSection.type ?? ''
        const targetType = targetSection.type ?? ''
        // Plan 153 Epic 03: prefer explicit targetGroupId from iframe over derived default
        const derivedGroupId = getDefaultGroupId(targetType)
        const finalTargetGroupId = targetGroupId ?? derivedGroupId

        // Width adaptation (Plan 150 Epic 02):
        // - Leaving naturalSizing → use block-type-aware width defaults
        // - Entering naturalSizing → set widthPct: 0 (flex: 0 0 auto)
        const leavingNatural = isNaturalSizingSection(sourceType) && !isNaturalSizingSection(targetType)
        const enteringNatural = !isNaturalSizingSection(sourceType) && isNaturalSizingSection(targetType)

        // Group-aware order: for grouped sections, only count blocks in the target group
        const existingBlocks = targetSection.blocks ?? []
        const appendOrder = (() => {
          if (targetGroupId) {
            const groupBlocks = existingBlocks.filter(b => b.layout.groupId === targetGroupId)
            return groupBlocks.length > 0 ? Math.max(...groupBlocks.map(b => b.layout.order)) + 1 : 0
          }
          return existingBlocks.length
        })()

        const movedBlock: BlockNode = {
          ...block,
          layout: {
            ...block.layout,
            groupId: finalTargetGroupId, // undefined for non-grouped, explicit or default for grouped
            order: insertAtIndex >= 0 ? insertAtIndex : appendOrder,
            ...(leavingNatural ? { widthPct: clampWidthPctForStandardSection(block), heightPx: undefined } : {}),
            ...(enteringNatural ? { widthPct: 0 } : {}),
          },
        }

        // Push compound undo for BOTH sections BEFORE mutation (atomic Cmd+Z restore)
        // Source gets the ORIGINAL blocks (with the moved block still present) so undo restores it
        // Target gets the ORIGINAL blocks (before insertion) so undo removes the moved block
        o.onPushCompoundUndo?.([
          { sectionEditorId: sourceEditorId, previousBlocks: [...sourceSection.blocks] },
          ...(targetSection.blocks ? [{ sectionEditorId: targetEditorId, previousBlocks: [...targetSection.blocks] }] : []),
        ])

        // Remove from source
        o.updateSection(sourceEditorId, { blocks: sourceBlocksAfter })

        // Insert into target
        const targetBlocks = [...(targetSection.blocks ?? [])]
        const idx = insertAtIndex >= 0 ? insertAtIndex : targetBlocks.length
        targetBlocks.splice(idx, 0, movedBlock)
        // Re-normalize order per group (blocks sorted by groupId then order at render time)
        const groupCounters = new Map<string, number>()
        const reordered = targetBlocks.map(b => {
          const gid = b.layout.groupId ?? '__default__'
          const orderIdx = groupCounters.get(gid) ?? 0
          groupCounters.set(gid, orderIdx + 1)
          return { ...b, layout: { ...b.layout, order: orderIdx } }
        })
        o.updateSection(targetEditorId, { blocks: reordered })

        // Cross-section move: do NOT skip CONFIG_UPDATE — iframe needs to re-render with new block layout
        o.cosmeticSkipCountRef.current = 0
      }

      // Deselect any selected block after cross-section move
      setSelectedBlock(null)
    },
    [],
  )

  /**
   * Handle block width change from sidebar dropdown.
   * Updates local state and lets CONFIG_UPDATE flow update the iframe.
   */
  const handleBlockWidthChange = useCallback(
    (blockId: string, widthPct: number) => {
      if (!selectedBlock) return
      const { sectionsRef, findByEditorId, updateSection, cosmeticSkipCountRef } = opts

      const editorId = sectionPathToEditorId(sectionsRef.current, selectedBlock.sectionPath)
      if (!editorId) return

      const lookup = findByEditorId(editorId)
      const section = lookup?.section
      if (!section?.blocks) return

      const updated = section.blocks.map((b: BlockNode) =>
        b.id === blockId ? { ...b, layout: { ...b.layout, widthPct } } : b,
      )

      updateSection(editorId, { blocks: updated })
      cosmeticSkipCountRef.current = 1
      setSelectedBlock((prev) => (prev ? { ...prev, widthPct } : null))
    },
    [opts, selectedBlock],
  )

  return { selectedBlock, selectBlock, deselectBlock, handleBlockUpdate, handleBlockWidthChange }
}
